import type {AriaTabProps} from '@react-types/tabs';
import type {DOMAttributes, FocusableElement} from '@react-types/shared';
import type {RefObject} from 'react';
import {filterDOMProps, mergeProps} from '../utils';
import {useSelectableItem} from '../selection';
import type {TabListState} from '../../@react-stately/tabs';

import {generateId} from './utils';

export interface TabAria {
  /** Props for the tab element. */
  tabProps: DOMAttributes;
  /** Whether the tab is currently selected. */
  isSelected: boolean;
  /** Whether the tab is disabled. */
  isDisabled: boolean;
  /** Whether the tab is currently in a pressed state. */
  isPressed: boolean;
}

/**
 * Provides the behavior and accessibility implementation for a tab.
 * When selected, the associated tab panel is shown.
 */
export function useTab<T>(
  props: AriaTabProps,
  state: TabListState<T>,
  ref: RefObject<FocusableElement>,
): TabAria {
  const {key, isDisabled: propsDisabled, shouldSelectOnPressUp} = props;
  const {selectionManager: manager, selectedKey} = state;

  const isSelected = key === selectedKey;

  const isDisabled =
    propsDisabled || state.isDisabled || state.disabledKeys.has(key);
  const {itemProps, isPressed} = useSelectableItem({
    selectionManager: manager,
    key,
    ref,
    isDisabled,
    shouldSelectOnPressUp,
    linkBehavior: 'selection',
  });

  const tabId = generateId(state, key, 'tab');
  const tabPanelId = generateId(state, key, 'tabpanel');
  const {tabIndex} = itemProps;

  const item = state.collection.getItem(key);
  const domProps = filterDOMProps(item?.props, {isLink: !!item?.props?.href});
  delete domProps.id;

  return {
    tabProps: mergeProps(domProps, itemProps, {
      id: tabId,
      'aria-selected': isSelected,
      'aria-disabled': isDisabled || undefined,
      'aria-controls': isSelected ? tabPanelId : undefined,
      tabIndex: isDisabled ? undefined : tabIndex,
      role: 'tab',
    }),
    isSelected,
    isDisabled,
    isPressed,
  };
}
